/**
 * Block 12 PR-B Phase B4 Step 3 — Section В additional row metadata.
 *
 * Russian labels + units per `CalculatorAdditional` enum id. Single source of
 * truth shared между UI rendering (`<SectionAdditional>`) и persistence layer
 * (`useAnketaPersist` Step 4 — encode toggled additionals в payload.freeItems).
 *
 * Если в Phase 9+ добавится новый additional kind — обнови `applicableAdditional`
 * в methodology JSON + добавь сюда строку. Items не listed в `applicableAdditional`
 * методики игнорируются UI (defensive против server drift, см. SectionAdditional).
 *
 * Reference: ADR-0021 «Block 12 Section В applicableAdditional enum extension»
 *            + методика §2 Section В per-anketa.
 * Source for items labels/units: docs/prototypes/screen-2.12.{2,3,4,5,6,7}-calc.html
 *            Section «Дополнительные элементы».
 *
 * Hotfix: Bug #6.2 + Bug #8 (Block 12 audit 2026-05-15) —
 *   - +3 items per ADR-0021 (basket-pullout / foldable-facade / trash-bin)
 *   - JSDoc false ref §1.4.2 (это «Расходники монтажа», не Section В) исправлен.
 *
 * Runtime invariant `Object.keys(ADDITIONAL_META) === CalculatorAdditionalSchema.options`
 * (set equality — два-сторонний drift) проверяется тестом в
 * `additionalMeta.test.ts`. Это ловит и orphan meta keys, и missing meta для
 * новых enum values.
 */

export const ADDITIONAL_META: Record<string, { label: string; unit: string }> = {
  // Existing 7 items (Phase B4 Step 3)
  'facade-radius': { label: 'Фасад радиусный', unit: 'шт' },
  'aluminum-glass-vitrine': { label: 'Витрина в алюминиевом профиле', unit: 'м²' },
  'metallic-legs': { label: 'Металлические опоры', unit: 'шт' },
  'decorative-feet': { label: 'Ножки (ТВ-зона)', unit: 'шт' },
  'dishwasher-top': { label: 'Посудосушилка верхнего шкафа', unit: 'шт' },
  'dishwasher-bottom': { label: 'Посудосушилка нижнего шкафа', unit: 'шт' },
  'laundry-basket': { label: 'Корзина для белья', unit: 'шт' },
  // Hotfix Step 10 per ADR-0021 — 3 новых items для Kitchen + BathCabinet Section В
  'basket-pullout': { label: 'Корзина выдвижная', unit: 'шт' },
  'foldable-facade': { label: 'Складной фасад', unit: 'шт' },
  'trash-bin': { label: 'Мусорное ведро', unit: 'шт' },
};

/**
 * PR-calc-buttons-unify (Aibek 2026-06-17, ADR-0099) — per-id deeplink target
 * для жёлтой кнопки «Найти и узнать цену» в Section В (Доп.элементы):
 *   partner: facade-radius, aluminum-glass-vitrine (специзделия → /partners)
 *   furniture: остальные 8 (фурнитура → /furniture, Block 5; ADR-0100)
 * Лейбл кнопки одинаков для всех; target определяет Container через
 * `onDeeplink(rowId)` + lookup `additionalDeeplink`. Живёт здесь (а не в
 * SectionAdditional.tsx) — single source of truth + react-refresh clean
 * (компонентный файл не должен экспортировать non-component helpers).
 */
const ADDITIONAL_DEEPLINK: Record<string, 'partner' | 'furniture'> = {
  'facade-radius': 'partner',
  'aluminum-glass-vitrine': 'partner',
  'metallic-legs': 'furniture',
  'decorative-feet': 'furniture',
  'dishwasher-top': 'furniture',
  'dishwasher-bottom': 'furniture',
  'laundry-basket': 'furniture',
  'basket-pullout': 'furniture',
  'foldable-facade': 'furniture',
  'trash-bin': 'furniture',
};

/** Per-id deeplink target для Доп.элементов. Default `furniture` (фурнитура). */
export function additionalDeeplink(id: string): 'partner' | 'furniture' {
  return ADDITIONAL_DEEPLINK[id] ?? 'furniture';
}
